import { useEffect, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageContainer from "../components/layout/PageContainer";
import SectionTitle from "../components/common/SectionTitle";
import Input from "../components/common/Input";
import Button from "../components/common/Button";
import { formatDate, formatTime } from "../utils/formatDate";
import formatCurrency from "../utils/formatCurrency";
import * as bookingService from "../services/bookingService";
import {
  JAGUAR_HENNA_DISCLAIMER,
  MOBILE_TRAVEL_EXPENSES_DISCLAIMER,
  SERVICE_MODE,
} from "../utils/constants";

const STUDIO_LOCATION =
  "The artist will email or text the studio address within 48 hours before your appointment.";

const buildAddressText = (onsiteAddress) => {
  if (!onsiteAddress) return "";

  return [
    onsiteAddress.line1,
    onsiteAddress.line2,
    onsiteAddress.city,
    onsiteAddress.state,
    onsiteAddress.zip,
  ]
    .filter(Boolean)
    .join(", ");
};

const normalizeBookingItems = (booking) => {
  if (Array.isArray(booking?.bookingItems) && booking.bookingItems.length > 0) {
    return booking.bookingItems;
  }

  if (booking?.service) {
    return [
      {
        service: booking.service,
        hennaType: booking.hennaType || booking.service.hennaType || "",
        numOfAdults: booking.numOfAdults ?? 0,
        numOfChildren: booking.numOfChildren ?? 0,
        partySize: booking.partySize ?? 0,
        bookingDurationMinutes: booking.bookingDurationMinutes ?? 0,
        bookingPrice: booking.bookingPrice ?? 0,
        sortOrder: 0,
      },
    ];
  }

  return [];
};

const getBookedServicesText = (booking) => {
  const items = normalizeBookingItems(booking);

  if (items.length === 0) return "—";

  return items
    .map((item, index) => {
      const serviceName = item?.service?.serviceName || `Service ${index + 1}`;
      const hennaType =
        item?.hennaType || item?.service?.hennaType || "Henna type not selected";
      const price =
        item?.bookingPrice !== undefined && item?.bookingPrice !== null
          ? formatCurrency(item.bookingPrice)
          : "—";
      const duration =
        item?.bookingDurationMinutes !== undefined &&
        item?.bookingDurationMinutes !== null
          ? `${item.bookingDurationMinutes} minutes`
          : "—";

      return `${serviceName} — ${hennaType} — ${price} — ${duration}`;
    })
    .join(" | ");
};

const hasJaguarItem = (booking) => {
  return normalizeBookingItems(booking).some(
    (item) => item?.hennaType === "Jaguar" || item?.service?.hennaType === "Jaguar"
  );
};

const BookingConfirmation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [code, setCode] = useState(searchParams.get("code") || "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const goToLookupStatus = (booking) => {
    const addressText = buildAddressText(booking?.onsiteAddress);
    const bookedServicesText = getBookedServicesText(booking);

    navigate("/action-status", {
      replace: true,
      state: {
        status: "success",
        eyebrow: "Booking Found",
        title: "Your booking details were found",
        message:
          "We found an appointment that matches the confirmation code you entered.",
        note:
          "Please keep your confirmation code somewhere safe in case you need to review or cancel your appointment later.",
        details: [
          {
            label: "Confirmation Code",
            value: booking?.bookingConfirmationCode || "—",
          },
          {
            label: "Booked Services",
            value: bookedServicesText,
          },
          {
            label: "Date",
            value: booking?.bookingDate ? formatDate(booking.bookingDate) : "—",
          },
          {
            label: "Time",
            value:
              booking?.startTime && booking?.endTime
                ? `${formatTime(booking.startTime)} - ${formatTime(
                    booking.endTime
                  )}`
                : "—",
          },
          {
            label: "Status",
            value: booking?.status || "—",
          },
          {
            label: "Party Size",
            value:
              booking?.partySize !== undefined && booking?.partySize !== null
                ? String(booking.partySize)
                : "—",
          },
          {
            label: "Price",
            value:
              booking?.bookingPrice !== undefined && booking?.bookingPrice !== null
                ? formatCurrency(booking.bookingPrice)
                : "—",
          },
          {
            label: "Duration",
            value:
              booking?.bookingDurationMinutes !== undefined &&
              booking?.bookingDurationMinutes !== null
                ? `${booking.bookingDurationMinutes} minutes`
                : "—",
          },
          {
            label: "Appointment Type",
            value: booking?.serviceMode || "—",
          },
          {
            label:
              booking?.serviceMode === SERVICE_MODE.STUDIO
                ? "Studio Location"
                : "Appointment Address",
            value:
              booking?.serviceMode === SERVICE_MODE.STUDIO
                ? STUDIO_LOCATION
                : addressText || booking?.locationNotes || "—",
          },
          ...(booking?.serviceMode === SERVICE_MODE.MOBILE
            ? [
                {
                  label: "Travel Notice",
                  value: MOBILE_TRAVEL_EXPENSES_DISCLAIMER,
                },
              ]
            : []),
          ...(booking?.serviceMode === SERVICE_MODE.MOBILE && booking?.locationNotes
            ? [
                {
                  label: "Location Notes",
                  value: booking.locationNotes,
                },
              ]
            : []),
          ...(hasJaguarItem(booking)
            ? [
                {
                  label: "Jaguar Notice",
                  value: JAGUAR_HENNA_DISCLAIMER,
                },
              ]
            : []),
        ],
        actions: [
          {
            label: "Cancel This Booking",
            to: `/booking-cancel?code=${encodeURIComponent(
              booking?.bookingConfirmationCode || ""
            )}`,
            variant: "primary",
          },
          {
            label: "Back to Home",
            to: "/",
            variant: "secondary",
          },
        ],
      },
    });
  };

  const handleLookup = async (event) => {
    if (event) event.preventDefault();

    setError("");

    const trimmedCode = code.trim();

    if (!trimmedCode) {
      setError("Please enter your confirmation code.");
      return;
    }

    try {
      setLoading(true);

      const data = await bookingService.getBookingByConfirmationCode(trimmedCode);

      if (data?.statusCode && data.statusCode !== 200) {
        throw new Error(data.message || "Booking lookup failed.");
      }

      const booking = data?.booking || null;

      if (!booking) {
        throw new Error("We could not find a booking with that confirmation code.");
      }

      goToLookupStatus(booking);
    } catch (err) {
      setError(err?.message || "We could not find your booking.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const codeFromQuery = searchParams.get("code") || "";
    if (!codeFromQuery) return;

    setCode(codeFromQuery);
  }, [searchParams]);

  return (
    <div className="page-section">
      <PageContainer width="md">
        <div className="mx-auto max-w-2xl content-card-lg p-8">
          <SectionTitle
            eyebrow="Booking Lookup"
            title="Find your appointment"
            subtitle="Enter your confirmation code to review your booking details."
            className="mb-8"
          />

          {error ? (
            <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-medium text-red-700">{error}</p>
            </div>
          ) : null}

          <form onSubmit={handleLookup} className="space-y-5">
            <Input
              label="Confirmation Code"
              name="confirmationCode"
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
                setError("");
              }}
              placeholder="Enter your confirmation code"
              leftElement={<FiSearch size={18} />}
              required
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={loading}
            >
              Find My Booking
            </Button>
          </form>
        </div>
      </PageContainer>
    </div>
  );
};

export default BookingConfirmation;